import React, { useEffect } from "react";
import { useRouteMatch } from "react-router-dom";

// @material-ui/core components
import { CircularProgress, Typography, Box } from "@material-ui/core";
import { CloudUpload, Create, DoneAll, Error } from "@material-ui/icons";

// core components
import CreateCourseCard from "views/Components/CreateCourseCard";

// context and APIs
import NotificationContext from "views/Components/Context.js";
import {
  getAllModelValidationProjectsAPI,
  createModelValidationProjectAPI,
  cloneModelValidationProjectAPI,
  inviteUserToModelValidationProjectAPI,
  deleteModelValidationProjectAPI,
  updateModelValidationProjectTagsAPI,
} from "views/APIs/ToolboxUtility";
import AssetDashboard from "views/Components/AssetDashboard";

// styles
import styles from "assets/jss/material-dashboard-pro-react/views/dashboardStyle.js";
import { makeStyles } from "@material-ui/core/styles";
import { fetchAllProjectsAPI } from "views/APIs/ProjectsUtility";
import CreateModelValidationProjectModal from "./CreateModelValidationProjectModal";

const useStyles = makeStyles({
  ...styles,
});

export default function ValidationDesignDashboard() {
  // Import images from assets
  const images = require.context("assets/img/create_cards_16_9/", true);
  const path = useRouteMatch().path;
  const classes = useStyles();

  const [project_id, setProjectId] = React.useState("");

  // Context and state variables
  const { showNotification } = React.useContext(NotificationContext);

  // State variables for project creation and management
  const [createProjectModal, setCreateProjectModal] = React.useState(false);
  const [newProjectDetails, setNewProjectDetails] = React.useState({
    name: "",
    description: "",
  });
  const [
    allModelValidationProjects,
    setAllModelValidationProjects,
  ] = React.useState([]);
  const [
    initialModelValidationProjects,
    setInitialModelValidationProjects,
  ] = React.useState([]);
  const [loading, setLoading] = React.useState({ loading: false, message: "" });
  const [projectCreationLoader, setProjectCreationLoader] = React.useState(
    false
  );
  const [projectAdded, setProjectAdded] = React.useState(false);
  const [projects, setProjects] = React.useState([]);
  const [selectedProject, setSelectedProject] = React.useState("");

  // Handle project deletion
  const handleDeleteProject = async (projectData) => {
    const formData = new FormData();
    formData.append("project_id", projectData._id);
    try {
      await deleteModelValidationProjectAPI(formData);
      setAllModelValidationProjects(
        allModelValidationProjects.filter(
          (project) => project._id !== projectData._id
        )
      );
      showNotification("Project deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  useEffect(() => {
    const fetchAllProjects = async () => {
      try {
        setLoading({ loading: true, message: "Loading Projects..." });
        const data = await fetchAllProjectsAPI();
        setProjects(data);
        setSelectedProject(data?.[0]?._id || "");
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchAllProjects();
  }, []);

  // Handle project cloning
  const handleCloneProject = async (projectData) => {
    try {
      setLoading({ loading: true, message: "Cloning Project..." });
      const formData = new FormData();
      formData.append("project_id", projectData._id);
      const data = await cloneModelValidationProjectAPI(formData);
      setAllModelValidationProjects((prevProjects) => [
        ...prevProjects,
        _mapResourceToAppType(data),
      ]);
      setProjectAdded(true);
      showNotification("Project cloned successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
      setLoading({ loading: false, message: "" });
    }
  };

  useEffect(() => {
    const fetchAllModelValidationProjects = async () => {
      try {
        setLoading({ loading: true, message: "Loading Projects..." });
        const data = await getAllModelValidationProjectsAPI();
        const mappedData = data.map(_mapResourceToAppType);

        setAllModelValidationProjects(mappedData);
        setInitialModelValidationProjects(mappedData);
      } catch (error) {
        showNotification(error, "danger");
      } finally {
        setLoading({ loading: false, message: "" });
      }
    };

    fetchAllModelValidationProjects();
  }, []);

  // Handle project creation
  const handleCreateProject = async () => {
    setProjectCreationLoader(true);

    try {
      // Prepare form data
      const formData = new FormData();
      formData.append("title", newProjectDetails.name);
      formData.append("description", newProjectDetails.description);

      // Create project via API call
      const data = await createModelValidationProjectAPI(formData);
      window.open(`/create/model_validation/model/${data._id}`, "_blank");

      setAllModelValidationProjects((prevProjects) => [
        ...prevProjects,
        _mapResourceToAppType(data),
      ]);
      setProjectAdded(true);
      showNotification("Project created successfully", "success");
    } catch (error) {
      showNotification(
        `Failed to create project: ${error.message || error}`,
        "danger"
      );
    } finally {
      setProjectCreationLoader(false);
      handleCreateProjectModalClose();
    }
  };

  const handleSaveLabel = async (data) => {
    try {
      const formData = new FormData();
      formData.append("project_id", data._id);
      if (data.tags.length === 0) {
        formData.append("tags", []);
      }
      // Append each tag separately
      else {
        data.tags.forEach((tag) => formData.append("tags", tag));
      }
      await updateModelValidationProjectTagsAPI(formData);
      setAllModelValidationProjects((prevProjects) =>
        prevProjects.map((project) =>
          project._id === data._id ? { ...project, tags: data.tags } : project
        )
      );
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleInviteSend = async (data) => {
    try {
      const formData = new FormData();
      formData.append("project_id", data._id);
      formData.append("email", data.email);

      const response = await inviteUserToModelValidationProjectAPI(formData);
      const updatedProjectId = response.project_id;

      // Find the target asset
      const updatedAsset = allModelValidationProjects.find(
        (asset) => asset._id === updatedProjectId
      );
      if (!updatedAsset) {
        console.warn(
          "⚠️ Could not find the matching asset for ID:",
          updatedProjectId
        );
        return;
      }

      // Create a new invitee object
      const newInvitee = {
        invitee_name: response.name,
        email: response.email,
        status: "pending",
      };
      // Update or append the invitee
      const updatedInvitees = (updatedAsset.invitees || []).map((invitee) =>
        invitee.email === newInvitee.email
          ? { ...invitee, status: "pending" } // Update status if email matches
          : invitee
      );

      // If no match was found in map, add the new invitee
      const emailExists = updatedInvitees.some(
        (inv) => inv.email === newInvitee.email
      );
      if (!emailExists) {
        updatedInvitees.push(newInvitee);
      }

      const newAsset = {
        ...updatedAsset,
        invitees: updatedInvitees,
      };

      // Update the allModelValidationProjects state
      const updatedProjects = allModelValidationProjects.map((project) =>
        project._id === updatedProjectId ? newAsset : project
      );

      setAllModelValidationProjects(updatedProjects);
      showNotification("Invite sent successfully", "success");
    } catch (error) {
      console.error("Error sending invite:", error);
      showNotification(error?.message || "Failed to send invite", "danger");
    }
  };

  // Close project creation modal and reset state
  const handleCreateProjectModalClose = () => {
    setCreateProjectModal(false);
    setNewProjectDetails({
      name: "",
      description: "",
    });
  };

  // Get status icon based on project status
  const getStatusIcon = (status) => {
    switch (status) {
      case "In Design Phase":
        return <Create style={{ color: "#435966" }} />;
      case "Ready for Publishing":
        return <CloudUpload style={{ color: "#435966" }} />;
      case "Published":
        return <DoneAll style={{ color: "#435966" }} />;
      case "Publishing Failed":
        return <Error style={{ color: "#435966" }} />;
      default:
        return null;
    }
  };

  const _mapResourceToAppType = (resource) => {
    // Destructure the resource to exclude project_name and project_description
    const { title, description, ...rest } = resource;

    return {
      ...rest, // Spread all remaining properties
      name: title, // Add name property
      description: description, // Add description property
    };
  };

  // Show loading spinner while fetching projects
  if (loading.loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="60vh"
      >
        <CircularProgress
          color="quCreateColor"
          className={classes.loaderClass}
        />
        <Typography
          variant="h6"
          style={{ marginLeft: 16, textTransform: "none" }}
        >
          {loading.message}
        </Typography>
      </Box>
    );
  }

  return (
    <div>
      <CreateModelValidationProjectModal
        newProjectDetails={newProjectDetails}
        setNewProjectDetails={setNewProjectDetails}
        createProjectModal={createProjectModal}
        handleCreateProjectModalClose={handleCreateProjectModalClose}
        handleCreateProject={handleCreateProject}
        projectCreationLoader={projectCreationLoader}
      />

      <AssetDashboard
        path={path}
        welcomeTitle="Model Validation Design Dashboard"
        welcomeSubtitle="Start by creating a new model validation project or reviewing your existing projects."
        allAssets={allModelValidationProjects}
        setAllAssets={setAllModelValidationProjects}
        initialAssets={initialModelValidationProjects}
        noAssetMessage="Create or review your model validation projects to get started."
        linkPath="/model/"
        getStatusIcon={getStatusIcon}
        CreateAssetCard={CreateCourseCard}
        setCreateAssetModal={setCreateProjectModal}
        createAssetButtonText="Create Project"
        handleSaveLabel={handleSaveLabel}
        handleInviteSend={handleInviteSend}
        asset_identifier="Model Validation Plan"
        handleDeleteAsset={handleDeleteProject}
        handleCloneAsset={handleCloneProject}
        assetAdded={projectAdded}
        setAssetAdded={setProjectAdded}
        setInitialAssets={setInitialModelValidationProjects}
        workflowConfig={{
          type: "model_val",
          title: "Model Validation Workflow",
          description: `Validate and document models in a structured process aligned with governance and regulatory requirements.`,
          steps: [
            {
              label: "1. Setup project & scope",
              body:
                "Enter project details, add relevant files, and define the validation scope.",
            },
            {
              label: "2. Specify model card & system details",
              body:
                "Capture vendor documentation, model cards, design and training details.",
            },
            {
              label: "3. Validate policy & compliance regulations",
              body:
                "Specify internal governance requirements and external regulatory guidance.",
            },
            {
              label: "4. Review the validation document",
              body:
                "Review a consolidated overview of the project and generate the model validation " +
                "document.",
            },
          ],
          videoUrl:
            "https://qucoursify.s3.us-east-1.amazonaws.com/qcreate/demos/model-validation-demo.mp4",
          examples: [],
        }}
      />
    </div>
  );
}
